import React, { useState, useEffect, forwardRef } from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, mixed, string, date, boolean } from '../../../Helpers/Validators/customValidators';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { sub } from 'date-fns';
import States, { compare } from '../../../Helpers/Lists/States';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { CheckboxInput, TextInput, SelectInput } from '../../../Helpers/Inputs';
import BackNext from '../../Common/SubComponents/BackNext';
import GenderButtons from '../../Common/SubComponents/GenderButtons';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';

import '../../Common/BasicInfo.scss';

  const BasicInfo = forwardRef((props, ref) => {
  var states = States();
  states.push({ label: "Puerto Rico", value: "PR" });
  states = states.sort(compare);

  const [noValidate] = useState(props.noValidate || false);

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: BasicInfo.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  useEffect(() => {
    if (noValidate) return;
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'require'});
    setValue('require', true);
    register({ name: 'gender' });
    setValue('gender', props.formState.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', props.formState.birthdate);
    register({ name: 'state' });
    setValue('state', props.formState.state);
    register({ name: 'usResident' });
    setValue('usResident', props.formState.usResident);
    register({ name: 'noValidate' });
    setValue('noValidate', noValidate);
  }, []);

  const onSubmit = () => {
    if (props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      var basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
      basicInfoFromSessionStorage.state = props.formState.state;
      basicInfoFromSessionStorage.usResident = props.formState.usResident;

      setBasicInfo(basicInfoFromSessionStorage);
      props.onNext();
    }
  }

  return (
    <div className="basic-info d-flex justify-content-center">
      {noValidate ?
        <BasicInfoForm formState={props.formState} handleValueChange={props.handleValueChange} onFieldChange={props.onFieldChange} hideBackNext={props.hideBackNext} logoBottom={props.logoBottom} errors={props.errors} register={props.register} states={states} />
        :
        <Form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" ref={ref}>
          <BasicInfoForm formState={props.formState} handleValueChange={handleValueChange} onFieldChange={props.onFieldChange} hideBackNext={props.hideBackNext} logoBottom={props.logoBottom} errors={errors} register={register} states={states} />
        </Form>
      }
    </div>
  );
});

export default BasicInfo;

function BasicInfoForm(props) {
  return (
    <div className="d-flex flex-column">
      <h2 className={`mx-auto ${!props.logoBottom ? 'd-none' : ''}`}>Coverage Details</h2>

      <div className={`d-flex justify-content-center ${props.logoBottom ? 'order-5' : 'order-1'}`}>
        <img src="/product-landing/supp/aflac-logo.png" alt="Aflac Logo" style={{ height: '73px', width: '160px' }} className="mb-2" />
      </div>

      <Form.Group className={`m-0 ${props.logoBottom ? 'order-1' : 'order-2'}`}>
        <GenderButtons
          selectedGender={props.formState.gender}
          onClickMale={() => props.handleValueChange('gender', 'M')}
          onClickFemale={() => props.handleValueChange('gender', 'F')}
          error={props.errors && props.errors.gender} />
      </Form.Group>

      <Form.Group className={props.logoBottom ? 'order-2' : 'order-3'}>
        <TextInput
          name="birthdate"
          id="birthdate"
          label="Date of Birth"
          value={props.formState.birthdate}
          errors={props.errors && props.errors.birthdate}
          inputRef={props.register}
          onChange={(e) => props.handleValueChange('birthdate', e.target.value)}
          InputProps={{
            inputComponent: InputMaskDate,
          }}
        />
      </Form.Group>

      <Form.Group className={props.logoBottom ? 'order-3' : 'order-4'}>
        <SelectInput
          name="state"
          id="state"
          value={props.formState.state}
          onChange={(e) => props.handleValueChange('state', e.target.value)}
          label="State"
          selectOptions={props.states}
          errors={props.errors && props.errors.state}
        />
      </Form.Group>


      <p className={props.logoBottom ? 'text-center order-7' : 'd-none'}>"We help with expenses health insurance doesn't cover so you can focus on recovery."</p>

      {!props.hideBackNext &&
        <BackNext
          hideBack={true}
          onBack={props.onBack}
          hide={props.hideBackNext}
          className="order-8"
          continueLabel={{text: "Get a Quote"}}
        />
      }
    </div>
  );

}

BasicInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func,
  onFieldChange: PropTypes.func.isRequired,
  hideBackNext: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired,
  logoBottom: PropTypes.bool,
  noValidate: PropTypes.bool
};

BasicInfoForm.propTypes = {
  formState: PropTypes.object.isRequired,
  handleValueChange: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBackNext: PropTypes.bool,
  logoBottom: PropTypes.bool,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired
};

BasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    require: boolean(),
    gender: string()
      .when('require', {
        is: true,
        then: string().required('Gender is required.'),
        otherwise: string().notRequired()
      }),
    birthdate: date()
      .transform(emptyStringToUndefined)
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min }), `Minimum age is ${ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .when('require', {
        is: true,
        then: date().transform(emptyStringToUndefined).typeError('Invalid date.').required('Birthdate is required.'),
        otherwise: date().transform(emptyStringToUndefined).notRequired()
      })
    ,
    state: string()
      .when('require', {
        is: true,
        then: string().required('State is required.'),
        otherwise: string().notRequired()
      }),
  })
};

BasicInfo.DefaultFormState = (prefill = false, existingBasicInfo = null) => {
  return {
    gender: prefill ? 'M' : (existingBasicInfo && existingBasicInfo.gender) || '',
    birthdate: prefill ? '11/15/1970' : (existingBasicInfo && existingBasicInfo.birthdate) || '',
    state: prefill ? 'ID' : (existingBasicInfo && existingBasicInfo.state) || '',
    usResident: true,
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Supplemental/steps/BasicInfo.js